import { Button } from 'antd';

interface Props {
  /** Ghi đơn NGAY — không còn bước xác nhận trong Drawer. */
  onCheckout: () => void;
  /** Có ⇒ đơn đang giao cho ĐVVC: hiện thêm nút đẩy vận đơn ngay tại quầy. */
  onCheckoutAndDispatch?: () => void;
  /** TCR-1963 — có ⇒ hiện nút "Lưu đơn nháp" (giữ tồn, chưa thu tiền). */
  onSaveDraft?: () => void;
  checkoutDisabled: boolean;
  /** Đang chờ hãng trả mã vận đơn. */
  dispatching?: boolean;
}

/**
 * Cờ tạm ẩn nút "Tạo và Đi đơn" khỏi giao diện.
 *
 * Chỉ ẩn ở tầng hiển thị: `onCheckoutAndDispatch`, `handleCheckout({
 * dispatchShipping })` và cả đường đẩy vận đơn phía sau vẫn còn nguyên, nên bật
 * lại chỉ là đổi hằng này thành `true` (kèm bỏ `.skip` của
 * `describe('PaymentFooter — nhánh hai nút ĐVVC')` trong `PaymentFooter.test.tsx`
 * và các test end-to-end đã skip ở `POSPage.checkoutInline` / `shippingWiring`).
 *
 * Khai báo kiểu `boolean` chứ không để TS suy ra literal `false`: literal khiến
 * toàn bộ nhánh hai nút bị coi là code chết và mọi thứ bên trong hết được kiểm
 * kiểu — đúng lúc cần nó còn nguyên vẹn để bật lại.
 */
const SHOW_CHECKOUT_AND_DISPATCH: boolean = false;

/**
 * Chỉ còn nút bấm. Bảng tổng kết tiền trước đây nằm ở đây (`PaymentSummary`) đã
 * được `OrderInfoPanel` thay thế: hai khối cùng hiển thị tiền của một đơn nhưng
 * khác bộ dòng, giữ cả hai là mời hai con số lệch nhau.
 *
 * Chế độ ĐVVC (`onCheckoutAndDispatch` có mặt) tách làm hai nút: "Tạo đơn hàng"
 * ghi đơn NHƯ CŨ (thu ngân tự đẩy vận đơn sau ở màn Vận chuyển), "Tạo và Đi đơn"
 * ghi đơn RỒI đẩy luôn sang hãng ngay tại quầy — hai đường độc lập, một cú bấm
 * chỉ chọn MỘT trong hai, không phải bấm nối tiếp.
 *
 * Thứ tự và kiểu hai nút KHÔNG tuỳ ý: "Tạo và Đi đơn" trái + kiểu thường, "Tạo
 * đơn hàng" phải + xanh nổi bật. Guard: `PaymentFooter.test.tsx` đọc thứ tự DOM
 * và class/màu của từng nút.
 */
export function PaymentFooter({
  onCheckout,
  onCheckoutAndDispatch,
  onSaveDraft,
  checkoutDisabled,
  dispatching,
}: Props) {
  if (onCheckoutAndDispatch && SHOW_CHECKOUT_AND_DISPATCH) {
    // Khoá cả hai nút trong lúc hãng đang trả mã vận đơn — bấm "Tạo đơn hàng"
    // giữa lúc đó sẽ tạo đơn thứ hai cho cùng giỏ hàng.
    const disabled = checkoutDisabled || dispatching;
    return (
      <div
        className="pos-payment-footer"
        style={{
          borderTop: '1px solid #e0e0e0',
          flexShrink: 0,
          padding: '12px 16px',
          display: 'flex',
          gap: 8,
        }}
      >
        {/* TCR-1963 — nút nháp đứng đầu, cùng kiểu với nhánh một nút: giữ tồn,
            không thu tiền, không đẩy vận đơn. */}
        {onSaveDraft && (
          <Button
            type="default"
            size="large"
            style={{ flex: '0 0 40%', fontWeight: 700 }}
            disabled={disabled}
            onClick={onSaveDraft}
          >
            Lưu đơn nháp
          </Button>
        )}
        {/* TRÁI, kiểu thường: đường phụ — thu ngân chủ động chọn đẩy vận đơn
            ngay tại quầy. PHẢI, xanh nổi bật: hành động mặc định (ghi đơn), giữ
            đúng vị trí và màu của nút một-nút "Ghi hoá đơn và thanh toán" để tay
            thu ngân không phải học lại chỗ bấm khi đơn chuyển sang chế độ ĐVVC. */}
        <Button
          type="default"
          size="large"
          style={{ flex: 1, fontWeight: 700 }}
          disabled={disabled}
          loading={dispatching}
          onClick={onCheckoutAndDispatch}
        >
          Tạo và Đi đơn
        </Button>
        <Button
          type="primary"
          size="large"
          style={{
            flex: 1,
            background: disabled ? '#bdbdbd' : '#52c41a',
            borderColor: disabled ? '#9e9e9e' : '#52c41a',
            fontWeight: 700,
          }}
          disabled={disabled}
          onClick={onCheckout}
        >
          Tạo đơn hàng
        </Button>
      </div>
    );
  }

  return (
    <div
      className="pos-payment-footer"
      style={{
        borderTop: '1px solid #e0e0e0',
        flexShrink: 0,
        padding: '12px 16px',
        ...(onSaveDraft ? { display: 'flex', gap: 8 } : {}),
      }}
    >
      {/* TCR-1963 — TRÁI, kiểu thường: "Lưu đơn nháp" giữ tồn, KHÔNG thu tiền,
          KHÔNG mở dialog thanh toán. Chỉ hiện khi POSPage truyền `onSaveDraft`;
          không có thì render y hệt trước đây để không phá test hiện có. */}
      {onSaveDraft && (
        <Button
          type="default"
          size="large"
          style={{ flex: '0 0 40%', fontWeight: 700 }}
          disabled={checkoutDisabled}
          onClick={onSaveDraft}
        >
          Lưu đơn nháp
        </Button>
      )}
      <Button
        className="pos-payment-checkout-button"
        type="primary"
        size="large"
        block={!onSaveDraft}
        disabled={checkoutDisabled}
        onClick={onCheckout}
        style={{
          ...(onSaveDraft ? { flex: 1 } : {}),
          background: checkoutDisabled ? '#bdbdbd' : '#52c41a',
          borderColor: checkoutDisabled ? '#9e9e9e' : '#52c41a',
          color: checkoutDisabled ? '#ffffff' : undefined,
          opacity: checkoutDisabled ? 1 : undefined,
          fontWeight: 700,
          cursor: checkoutDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        Ghi hoá đơn và thanh toán
      </Button>
    </div>
  );
}
